/**
 * Role levels, as the server ranks them.
 * @file src/web/shared/utils/roles.ts
 *
 * A mirror of @bloomneo/appkit's default role hierarchy, which is what
 * auth.requireUserRoles() checks on every route: a role passes a requirement
 * when its level is the same or higher. The screens use this to avoid
 * offering an action the server will refuse — it is a courtesy, not the
 * guard. The server's check is the one that counts.
 *
 * Pure, so it is tested without a browser.
 */

export const ROLE_LEVEL: Record<string, number> = {
  'user.basic': 1,
  'user.pro': 2,
  'user.max': 3,
  'moderator.review': 4,   // Entity Moderator (a client's login)
  'moderator.approve': 5,  // Entity Admin (a client's login)
  'moderator.manage': 6,   // Professional Moderator (firm staff)
  'admin.tenant': 7,       // Professional Admin (the firm)
  'admin.org': 8,
  'admin.system': 9,       // Platform
};

/** "role.level" for a signed-in user, or null when nobody is signed in. */
export function roleOf(user: { role?: string | null; level?: string | null } | null | undefined): string | null {
  return user?.role && user?.level ? `${user.role}.${user.level}` : null;
}

/** True when `userRole` is at or above `required`. An unknown role is never enough. */
export function hasMinRole(userRole: string | null | undefined, required: string): boolean {
  const have = userRole ? ROLE_LEVEL[userRole] : undefined;
  const need = ROLE_LEVEL[required];
  return have !== undefined && need !== undefined && have >= need;
}

/** A client's own login (Entity Admin / Entity Moderator) rather than the firm's. */
export function isEntityUser(userRole: string | null | undefined): boolean {
  return userRole === 'moderator.approve' || userRole === 'moderator.review';
}
